/**
 * The child's IEP goals for ONE service, shaped for a therapy data sheet.
 *
 * A speech therapist opening the SLP sheet should find the child's speech goals
 * already on it, and an OT the OT goals — not a blank table they retype from the
 * plan every session. The plan is the source: a goal is tagged with the service
 * that delivers it (iep_goal.service_code) and carries the activities the
 * supervisor wrote for it (iep_goal.activities), so the sheet can seed itself.
 *
 * Seeding is by goal CODE and happens once per goal: the sheet's own standing
 * goals-config owns a row from the moment it lands, so activities a therapist
 * adds or removes in session survive the next load instead of being overwritten
 * by the plan on every open. A goal added to the IEP later shows up on the next
 * load; one removed from the IEP is left alone, because a therapist may still be
 * running it and losing their rows mid-session is worse than a stale row.
 */
import { getClientActiveTargets } from 'services/api/iep';
import { listSkillDomains } from 'services/api/skills';
import { activityLines } from 'utils/activityList';
import { getCachedClinicalData } from 'utils/offlineDB';

/**
 * Is this goal being worked on `onDate`?
 *
 * The same rule the ABA sheets apply to a target (see DailyMandMode): a goal has
 * to have been INTRODUCED on or before the session, and must not have ended -
 * either by running past its target date or by being achieved. A goal the
 * supervisor has written for next term is not something a therapist scores today,
 * and one that ended last month is not either.
 *
 * A START DATE IS REQUIRED. A goal without one has not been introduced, and an
 * un-introduced goal is not something a therapist scores today - the same rule the
 * ABA sheets apply to a target (see DailyMandMode). Imported goals arrive dateless
 * because the reports carry no per-goal dates; the import already tells the
 * reviewer to set them, and until they do the goal stays off the sheet rather than
 * appearing as work that was never scheduled.
 */
const isWorkableOn = (goal, onDate) => {
  const start = goal?.start_date ? new Date(goal.start_date) : null;
  if (!start || Number.isNaN(start.getTime())) return false;
  // Compare against the END of the session day, so a goal introduced today counts.
  const dayEnd = new Date(onDate);
  dayEnd.setHours(23, 59, 59, 999);
  if (start.getTime() > dayEnd.getTime()) return false;

  if (goal.achieved_date) return false;

  const end = goal?.target_date ? new Date(goal.target_date) : null;
  if (end && !Number.isNaN(end.getTime())) {
    const dayStart = new Date(onDate);
    dayStart.setHours(0, 0, 0, 0);
    if (end.getTime() < dayStart.getTime()) return false;
  }
  return true;
};

/**
 * Fetch the child's active-IEP goals for `serviceCode`.
 *
 * @param {string} childId     client UUID
 * @param {string} serviceCode clinical.ref_service.code ('speech_therapy', …)
 * @param {Date}   onDate      the session date the goals must be workable on
 * @returns {Promise<Array<{goalCode: string, goal: string, activities: string[], area: string}>>}
 *          Empty on any failure — a data sheet must still open when the IEP
 *          service is down, so this never throws at its caller.
 */
export const fetchServiceGoals = async (childId, serviceCode, onDate = new Date()) => {
  if (!childId || !serviceCode) return [];
  try {
    // The goal's skill domain IS the therapy area on these sheets ("Receptive
    // Language", "Sitting Endurance"), so the Area column can be filled from the
    // plan instead of the therapist re-picking what the supervisor already chose.
    // The domain list is fetched alongside; if it fails the area falls back to a
    // readable form of the code rather than blocking the goals themselves.
    // Offline, the active IEP comes from the per-child clinical cache (filled at
    // login and when the data-collection page loads) - without it a sheet opened
    // with no network had no goals, and offline entry could not start.
    const activeTargets = async () => {
      try {
        return await getClientActiveTargets(childId);
      } catch (err) {
        const cached = await getCachedClinicalData(childId).catch(() => null);
        if (Array.isArray(cached?.iepData?.goals)) return { goals: cached.iepData.goals };
        throw err;
      }
    };
    const [{ goals }, domains] = await Promise.all([activeTargets(), listSkillDomains().catch(() => [])]);
    const nameByCode = new Map(
      (Array.isArray(domains) ? domains : [])
        .filter((d) => d?.code && d?.name)
        .map((d) => [
          String(d.code)
            .replace(/^DOMAIN-/i, '')
            .toUpperCase(),
          d.name
        ])
    );
    const areaFor = (code) => {
      const bare = String(code || '')
        .replace(/^DOMAIN-/i, '')
        .trim();
      if (!bare) return '';
      return (
        nameByCode.get(bare.toUpperCase()) ||
        // "ST-RECEPTIVE-LANGUAGE" -> "Receptive Language", so an unknown code still
        // reads as words rather than as a database key. Split on spaces too: some
        // goals carry the domain NAME here ("Visual Motor"), and splitting on '-'
        // alone lowercased every word after the first ("Visual motor").
        bare
          .replace(/^(ST|OT)-/i, '')
          .split(/[-\s]+/)
          .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : w))
          .join(' ')
      );
    };
    // The active IEP carries the goals of EVERY version of itself, so the same
    // goal_code can appear more than once. Newest row first, so the dedupe below
    // keeps the current wording of a goal rather than a superseded one.
    const ordered = [...(goals || [])].sort((a, b) => {
      const ta = a?.created_at ? new Date(a.created_at).getTime() : 0;
      const tb = b?.created_at ? new Date(b.created_at).getTime() : 0;
      return tb - ta;
    });
    const seen = new Set();
    const out = [];
    for (const g of ordered) {
      if (g?.service_code !== serviceCode) continue;
      if (!isWorkableOn(g, onDate)) continue;
      const code = g.goal_code;
      if (!code || seen.has(code)) continue;
      const text = String(g.description || g.title || '').trim();
      if (!text) continue;
      seen.add(code);
      out.push({
        goalCode: code,
        goal: text,
        activities: activityLines(g.activities),
        area: areaFor(g.skill_domain_code)
      });
    }
    return out;
  } catch (err) {
    console.warn(`[iepServiceGoals] could not load ${serviceCode} goals:`, err?.message);
    return [];
  }
};

/**
 * Bring the sheet's rows up to date with the plan.
 *
 * Two jobs. A plan goal the sheet has never seen becomes a new row. A goal it
 * already has gets any activities the supervisor has added to it SINCE - which is
 * the whole reason this is a merge and not a one-time seed: a goal is usually
 * imported before anyone writes its activities, so a sheet that only seeded once
 * would show those rows empty forever.
 *
 * What it must never do is fight the therapist. Each row remembers which plan
 * activities have already been applied to it (`seededActivities`), so:
 *
 *   - an activity the supervisor adds later arrives on the next load;
 *   - an activity the therapist deleted stays deleted, because it is still on the
 *     applied list and is never offered again;
 *   - an activity the therapist typed themselves is never touched.
 *
 * A row whose goal has LEFT the plan is dropped. "Left the plan" means the goal no
 * longer passes isWorkableOn for this session - it was never dated, its start is in
 * the future, its end has passed, or it was achieved. Without this the date rule
 * would only ever govern NEW rows: a goal seeded on an earlier load stays on the
 * sheet for good, which is exactly how a sheet ends up showing twelve goals when
 * one is in range. Rows the therapist added by hand carry no goal code and are
 * never touched.
 *
 * The cost is real and worth knowing: if a supervisor moves a goal's dates out of
 * range, the row goes, and with it any activity the therapist had typed onto it.
 * It comes back from the plan when the goal is in range again.
 *
 * @param {Array} rows       the sheet's current rows (each may carry `goalCode`)
 * @param {Array} planGoals  output of fetchServiceGoals
 * @param {Function} makeRow (planGoal) => row, supplied by the sheet so each one
 *                           keeps its own row shape
 * @returns {{rows: Array, changed: boolean}} `changed` tells the sheet whether to
 *          write the config back; writing on every load would churn the record.
 */
export const mergeRowsFromPlan = (rows, planGoals, makeRow) => {
  const current = rows || [];
  const plan = planGoals || [];
  const planByCode = new Map(plan.map((g) => [g.goalCode, g]));
  let changed = false;

  const updated = current.map((row) => {
    const pg = row?.goalCode ? planByCode.get(row.goalCode) : null;
    if (!pg) return row;
    // A row written before this field existed has no record of what came from the
    // plan; treat everything on it as already applied, so an upgrade does not
    // re-add activities the therapist had removed.
    const applied = Array.isArray(row.seededActivities) ? row.seededActivities : activityLines(row.activities);
    const unseen = pg.activities.filter((a) => !applied.includes(a));
    // Fill the Area only when the row has none: a row seeded before the plan
    // carried an area gets one, and an area the therapist chose is never replaced.
    const needsArea = !String(row.area || '').trim() && Boolean(pg.area);
    if (unseen.length === 0 && !needsArea) return row;
    changed = true;
    return {
      ...row,
      area: needsArea ? pg.area : row.area,
      activities: unseen.length > 0 ? [...activityLines(row.activities), ...unseen] : row.activities,
      seededActivities: unseen.length > 0 ? [...applied, ...unseen] : applied
    };
  });

  // Drop plan-sourced rows whose goal is not workable this session. A row with no
  // goalCode was typed by the therapist and always stays.
  const planCodes = new Set(plan.map((g) => g.goalCode));
  const kept = updated.filter((row) => !row?.goalCode || planCodes.has(row.goalCode));
  if (kept.length !== updated.length) changed = true;

  const known = new Set(kept.map((r) => r?.goalCode).filter(Boolean));
  const fresh = plan.filter((g) => !known.has(g.goalCode)).map((g) => ({ ...makeRow(g), seededActivities: g.activities }));
  if (fresh.length > 0) changed = true;

  return { rows: fresh.length > 0 ? [...kept, ...fresh] : kept, changed };
};
